// THE SHOP'S STATE'S RULES ARRIVE BY FETCH, ONE STATE'S, NOT ALL 51 (TW-0505, 2026-10-29).
//
// Until this card every load of the form shipped the whole state contract in its bundle: on 2026-10-29, 741,066 of the main
// chunk's 1,472,149 bytes (126 KB of its 334 KB gzipped), every state's notices and minors rules in two languages, over a
// shop's wifi to a phone. A client needs one state's: the shop's. So the bundle carries none of it, and `./run.sh
// state-forms` writes one file per state beside the combined record (scripts/state-forms.py, the slice rule in its header),
// served by the form's own CDN at /_state-forms/<ST>.json, plus /_state-forms/all.json, the whole contract.
//
// HOW A PAGE GETS ITS STATE'S FILE. The shop's state is known the moment the shop is (App.tsx: the static `_shops/<sub>.json`
// first, the API's `/shop` second and winning), and App asks for that state's file then (`useStateRules`). If the API's state
// differs from the static file's, the API's is the state on the page, and its file is fetched in turn. The demo asks for the
// state its dial is on. A state's file that does not come (a 404, which the CDN answers as index.html with a 200, an answer
// that is not the state's, a stall past its timeout) is followed by all.json, which carries every state; only when both fail
// is the page told so. A page with no state, or with a state that is not one of the 51 (none today), fetches nothing: its
// rules are none, as they always were.
//
// NOTHING MAY BLOCK A SIGNING (the address suggestions' rule, address.ts). While the file is on its way the page is drawn as a
// page whose state is not known yet, the way it is drawn before the shop arrives: the "you" section, the standard questions,
// the service's clauses. Every reader below answers as for no state until the state's own rules are here (`ruleState`), so
// nothing a client reads claims a state rule the page has not got: the state's questions, notices and minors route appear
// when the file lands. A Submit pressed before then posts nothing and says one moment; if the file and the fallback both
// fail, the page says so in the client's language with a way to try again, and a Submit says the same and tries again.
// Form.tsx's half is two lines in handleSubmit and the line at the top of the form.
//
// THE TABLES ARE LIVE. stateForms.ts, minors.ts and cosmetic.ts hold the contract's tables at module level, the way they did
// when the JSON was imported (`const ITEMS = contract.items`); `RULES` below hands them the SAME objects, empty at load and
// filled in place when a state's file lands (`rebuild`), so not one of their readers changed. What a reader returns is read on
// every render, so the page draws the new rules on the render the landing causes. In jest the combined record is installed
// at setup (setupTests.ts), so every test reads all 51 states as the page used to; stateRules.test.tsx walks the fetch.
//
// THE WARM (infrastructure/prod/functions/warm-the-site/warm.py) has no entry for these, on purpose: they are objects in S3
// behind CloudFront like the bundle and `_shops/<sub>.json`, with no Lambda behind them and so no cold start, and a walk from
// one region fills one edge's cache, which a client elsewhere never reads.
import { useCallback, useEffect, useSyncExternalStore } from "react";

/** The 51 states (the 50 and DC), each with the file `./run.sh state-forms` writes for it, by name. stateForms.ts re-exports
 *  it as it always has; it lives here so this module needs nothing of the readers it fills. */
export const STATE_NAMES: Record<string, string> = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California", CO: "Colorado",
  CT: "Connecticut", DE: "Delaware", DC: "District of Columbia", FL: "Florida", GA: "Georgia", HI: "Hawaii",
  ID: "Idaho", IL: "Illinois", IN: "Indiana", IA: "Iowa", KS: "Kansas", KY: "Kentucky", LA: "Louisiana",
  ME: "Maine", MD: "Maryland", MA: "Massachusetts", MI: "Michigan", MN: "Minnesota", MS: "Mississippi",
  MO: "Missouri", MT: "Montana", NE: "Nebraska", NV: "Nevada", NH: "New Hampshire", NJ: "New Jersey",
  NM: "New Mexico", NY: "New York", NC: "North Carolina", ND: "North Dakota", OH: "Ohio", OK: "Oklahoma",
  OR: "Oregon", PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina", SD: "South Dakota",
  TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont", VA: "Virginia", WA: "Washington",
  WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
};

type Table = Record<string, unknown>;
/** The contract's tables, every one the generator writes (scripts/state-forms.py BY_STATE_TABLES, WHOLE_TABLES, EXAM_TABLE;
 *  stateRules.test.tsx holds this list to the combined record's keys). */
export type Rules = {
  items: Table;
  states: Table;
  names: Table;
  piercing_minors: Table;
  ear_exemption_reach: Table;
  tattoo_minors: Table;
  minor_requirements: Table;
  minor_cases: unknown[];
  digital_id: Table;
  notices: Table;
  destroy_after_years: Table;
  keep_years: Table;
  duties: Table;
  artist_attestations: Table;
  effective: Table;
  cosmetic: Table;
  /** TW-0628 slice 2 — the protections each state's rule applies (scripts/state-forms.py `protections`): the API's and the shop
   *  app's to read; the page carries it because a state's file is the contract cut to that state, and reads no entry of it */
  protections: Table;
};
export type RulesTable = keyof Rules;

/** The live tables the readers alias. Never reassigned, only emptied and filled in place. */
export const RULES: Rules = {
  items: {},
  states: {},
  names: {},
  piercing_minors: {},
  ear_exemption_reach: {},
  tattoo_minors: {},
  minor_requirements: {},
  minor_cases: [],
  digital_id: {},
  notices: {},
  destroy_after_years: {},
  keep_years: {},
  duties: {},
  artist_attestations: {},
  effective: {},
  cosmetic: {},
  protections: {},
};
export const RULES_TABLES = Object.keys(RULES) as RulesTable[];

/** Where the files are: the form's own host, the path `./run.sh deploy-form` publishes them under. */
export const STATE_FILES_PATH = "/_state-forms";
export const ALL_STATES_FILE = "all.json";
/** How long a page waits on a state's file (6 to 18 KB gzipped) before it asks for all.json, and on all.json (about 125 KB
 *  gzipped) before it says so. Far past slow 3G's two seconds a round trip, and short enough that a stall is said. */
export const STATE_FILE_TIMEOUT_MS = 10000;
export const ALL_STATES_TIMEOUT_MS = 20000;

export type RulesStatus = "ready" | "loading" | "failed";

/** The whole contract, once all.json landed (the fallback) or a test installed it: every state's rules are then here. */
let combined: Rules | null = null;
/** Each state's own file that landed, by its two letters. */
const slices = new Map<string, Rules>();
const pending = new Map<string, Promise<RulesStatus>>();
const failed = new Set<string>();
const listeners = new Set<() => void>();

const abbrOf = (state: string | null | undefined): string => `${state ?? ""}`.trim().toUpperCase();

/** Are `state`'s rules here: its own file landed, or all.json did, or it is a state no file is written for (whose rules are
 *  none, which is here already)? True for no state, which has no rules to wait on. */
export function rulesHere(state: string | null | undefined): boolean {
  const abbr = abbrOf(state);
  return !abbr || combined !== null || slices.has(abbr) || !(abbr in STATE_NAMES);
}

/** `state` as a reader should take it: its two letters once its rules are here, else "" (no state) while its file is on its
 *  way or never came. The one reader that tells "no state" from "a state with no rule for this" (minors.ts minorRefusal:
 *  `no_state`, which says nothing, against `state_no`, "Tennessee does not allow…") reads its state through this. */
export function ruleState(state: string | null | undefined): string {
  const abbr = abbrOf(state);
  return abbr && rulesHere(abbr) ? abbr : "";
}

/** Where a page stands for `state`: ready (its rules are here, or it needs none), loading (asked for, or about to be), or
 *  failed (its file and all.json both failed, and no ask is out again). */
export function statusOf(state: string | null | undefined): RulesStatus {
  const abbr = abbrOf(state);
  if (rulesHere(abbr)) return "ready";
  if (failed.has(abbr) && !pending.has(abbr)) return "failed";
  return "loading";
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notify(): void {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      /* one listener's throw never keeps the others from hearing */
    }
  });
}

/** The live tables, emptied and filled again from what is here: the whole contract when it is, else every state's own file
 *  merged (a table keyed by state carries one key a file; the vocabularies are the same in each; the exam's cases add up). */
function rebuild(): void {
  for (const key of RULES_TABLES) {
    const live = RULES[key];
    if (Array.isArray(live)) live.length = 0;
    else for (const own of Object.keys(live)) delete (live as Table)[own];
  }
  const sources = combined ? [combined] : Array.from(slices.values());
  for (const source of sources) {
    for (const key of RULES_TABLES) {
      const from = (source as Partial<Rules>)[key];
      const live = RULES[key];
      if (Array.isArray(live)) {
        if (Array.isArray(from)) live.push(...from);
      } else if (from && typeof from === "object" && !Array.isArray(from)) {
        Object.assign(live, from);
      }
    }
  }
}

/** Is this what a file for `abbr` must be: the contract's shape, naming `abbr` among its states? An answer that is not (the
 *  CDN's index.html under a 200, another state's file, a truncated body) is no file at all. */
function isRulesFor(data: unknown, abbr: string): data is Rules {
  if (!data || typeof data !== "object") return false;
  const d = data as Partial<Rules>;
  return Boolean(d.items && typeof d.items === "object" && d.names && typeof d.names[abbr] === "string");
}

async function fetchRules(url: string, abbr: string, timeoutMs: number): Promise<Rules | null> {
  const controller = typeof AbortController === "undefined" ? null : new AbortController();
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
  try {
    const res = await fetch(url, controller ? { signal: controller.signal } : undefined);
    if (!res.ok) return null;
    // the CDN answers a missing key with index.html and a 200 (infrastructure/prod/form.tf custom_error_response), and the
    // dev server does the same: an answer that is not JSON is no file here
    if (!(res.headers.get("content-type") || "").includes("json")) return null;
    const data: unknown = await res.json();
    return isRulesFor(data, abbr) ? data : null;
  } catch {
    // a dropped line, a timeout's abort, a body that is not JSON: the next source, then the page says so
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/** Ask for `state`'s rules: its own file, then all.json. One ask per state at a time (a second caller gets the first's
 *  answer); an ask for rules already here answers ready at once. Never throws. */
export function loadStateRules(state: string | null | undefined): Promise<RulesStatus> {
  const abbr = abbrOf(state);
  if (rulesHere(abbr)) return Promise.resolve("ready");
  const out = pending.get(abbr);
  if (out) return out;
  const ask = (async (): Promise<RulesStatus> => {
    const own = await fetchRules(`${STATE_FILES_PATH}/${abbr}.json`, abbr, STATE_FILE_TIMEOUT_MS);
    if (own) {
      slices.set(abbr, own);
      failed.delete(abbr);
      rebuild();
      return "ready";
    }
    const all = rulesHere(abbr) ? null : await fetchRules(`${STATE_FILES_PATH}/${ALL_STATES_FILE}`, abbr, ALL_STATES_TIMEOUT_MS);
    if (all) {
      combined = all;
      failed.delete(abbr);
      rebuild();
      return "ready";
    }
    if (rulesHere(abbr)) return "ready";
    failed.add(abbr);
    return "failed";
  })().finally(() => {
    pending.delete(abbr);
    notify();
  });
  pending.set(abbr, ask);
  // the ask is out: a page that read "failed" reads "loading" again until it answers
  notify();
  return ask;
}

/** The whole contract, installed at once: jest's setup (setupTests.ts), so every test reads all 51 states as the page used to. */
export function installAllStates(contract: unknown): void {
  combined = contract as Rules;
  rebuild();
  notify();
}

/** Back to a page that has asked for nothing: every table empty, nothing landed, nothing out. For a test that walks the fetch. */
export function forgetStateRules(): void {
  combined = null;
  slices.clear();
  pending.clear();
  failed.clear();
  rebuild();
  notify();
}

export type StateRulesView = { status: RulesStatus; retry: () => void };

/** The page's view of `state`'s rules, asking for them when the state is known and again when the phone comes back online
 *  after a failure. `retry` asks again (the "Try again" at the top of the form, and a Submit pressed after a failure). */
export function useStateRules(state: string | null | undefined): StateRulesView {
  const abbr = abbrOf(state);
  const status = useSyncExternalStore(subscribe, () => statusOf(abbr), () => statusOf(abbr));
  useEffect(() => {
    if (abbr) void loadStateRules(abbr);
  }, [abbr]);
  useEffect(() => {
    if (!abbr || typeof window === "undefined") return;
    const back = () => {
      if (statusOf(abbr) === "failed") void loadStateRules(abbr);
    };
    window.addEventListener("online", back);
    return () => window.removeEventListener("online", back);
  }, [abbr]);
  const retry = useCallback(() => {
    if (abbr) void loadStateRules(abbr);
  }, [abbr]);
  return { status, retry };
}
